import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  DialogContent,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
// import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { colors, typography } from '../../../styles/theme';
import { showroomMenuItemSx, showroomMenuPaperSx } from '../../Showroom.styles';
import {
  CAD_UPLOAD_NAME_MAX,
  cadPreviewLayerCandidates,
  cadPreviewSummaryRows,
  cadPreviewWarnings,
  countCadPreviewParcels,
} from '../../utils/cadUpload';
import CadPlanPreview from './CadPlanPreview';
import {
  actionsRowSx,
  errorTextSx,
  helperTextSx,
  nameFieldSx,
  previewCaptionSx,
  previewExpandButtonSx,
  primaryButtonSx,
  secondaryButtonSx,
  sectionLabelSx,
  summaryBoxSx,
  summaryLabelSx,
  summaryRowSx,
  summaryValueSx,
  // textButtonSx, // Comentado porque no se usa, a verificar mas adelante
  warningBoxSx,
  warningTextSx,
} from './CadUploadFlow.styles';

/**
 * Revisión del plano antes de confirmar. Mirar acá **no** crea nada: si el
 * usuario descarta, no se llama a `/createcadproject`. Al confirmar viaja
 * solo el `job_id` del parse más el nombre de la lista.
 *
 * @param {{
 *   preview: object,
 *   name: string,
 *   onNameChange: (name: string) => void,
 *   creating: boolean,
 *   reparsing: boolean,
 *   error: string,
 *   onConfirm: () => void,
 *   onDiscard: () => void,
 *   onRetryLayer: (layer: string) => void,
 * }} props
 */
export default function CadPreviewStep({
  preview,
  name,
  onNameChange,
  creating,
  reparsing,
  error,
  onConfirm,
  onDiscard,
  onRetryLayer,
}) {
  const [layerAnchor, setLayerAnchor] = useState(null);
  const [previewExpandedOpen, setPreviewExpandedOpen] = useState(false);

  const parcelCount = countCadPreviewParcels(preview);
  const summaryRows = cadPreviewSummaryRows(preview);
  const warnings = cadPreviewWarnings(preview);
  const layers = cadPreviewLayerCandidates(preview);
  const trimmed = name.trim();
  const busy = creating || reparsing;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography component="span" sx={sectionLabelSx}>
          Revisá el plano
        </Typography>
        <Typography sx={helperTextSx}>
          {parcelCount > 0
            ? `Encontramos ${parcelCount} ${parcelCount === 1 ? 'lote' : 'lotes'}. Si el dibujo no es tu loteo, descartalo y probá con otro archivo.`
            : 'No encontramos lotes en este plano. Revisá el archivo antes de crear la lista.'}
        </Typography>
        <Box sx={{ mt: 1.25 }}>
          <CadPlanPreview
            featureCollection={preview?.parcels}
            bounds={preview?.bounds}
            overlay={(
              <Tooltip title="Ampliar vista previa" placement="left" arrow>
                <span>
                  <IconButton
                    size="small"
                    aria-label="Ampliar vista previa"
                    onClick={() => setPreviewExpandedOpen(true)}
                    disabled={busy}
                    sx={previewExpandButtonSx}
                  >
                    <OpenInFullOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          />
          <Typography sx={previewCaptionSx}>
            Vista previa del plano. Aún no se creó nada.
          </Typography>
        </Box>
      </Box>

      {warnings.map((warning) => (
        <Box key={warning} sx={warningBoxSx}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
          <Typography sx={warningTextSx}>{warning}</Typography>
        </Box>
      ))}

      {summaryRows.length > 0 && (
        <Box sx={summaryBoxSx}>
          {summaryRows.map((row) => (
            <Box key={row.label} sx={summaryRowSx}>
              <Typography sx={summaryLabelSx}>{row.label}</Typography>
              <Typography sx={summaryValueSx}>{row.value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {layers.length > 0 && (
        <Box>
          {/* <Button
            startIcon={<LayersOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={(event) => setLayerAnchor(event.currentTarget)}
            disabled={busy}
            sx={textButtonSx}
          >
            Leer otra capa del plano
          </Button> */}
          <Menu
            anchorEl={layerAnchor}
            open={Boolean(layerAnchor)}
            onClose={() => setLayerAnchor(null)}
            slotProps={{ paper: { sx: showroomMenuPaperSx } }}
          >
            {layers.map((layer) => (
              <MenuItem
                key={layer}
                sx={showroomMenuItemSx}
                onClick={() => {
                  setLayerAnchor(null);
                  onRetryLayer(layer);
                }}
              >
                {layer}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      <Dialog
        open={previewExpandedOpen}
        onClose={() => setPreviewExpandedOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.backgroundDark,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Typography sx={{ color: colors.textPanelTitle, fontFamily: typography.fontFamily.heading, fontWeight: 600, fontSize: '15px' }}>
            Vista previa ampliada
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <IconButton
              aria-label="Cerrar"
              onClick={() => setPreviewExpandedOpen(false)}
              sx={{
                color: 'rgba(255,255,255,0.35)',
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 2.5 }}>
          <CadPlanPreview
            featureCollection={preview?.parcels}
            bounds={preview?.bounds}
            height={520}
          />
        </DialogContent>
      </Dialog>

      <Box>
        <Typography component="label" htmlFor="cad-lista-nombre" sx={sectionLabelSx}>
          Nombre de la lista
        </Typography>
        <TextField
          id="cad-lista-nombre"
          fullWidth
          placeholder="Escribí un nombre para la lista..."
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && trimmed && !busy) onConfirm();
          }}
          sx={nameFieldSx}
          inputProps={{ maxLength: CAD_UPLOAD_NAME_MAX }}
          disabled={busy}
        />
      </Box>

      {error ? <Typography sx={errorTextSx}>{error}</Typography> : null}

      <Box sx={actionsRowSx}>
        <Button variant="outlined" onClick={onDiscard} disabled={busy} sx={secondaryButtonSx}>
          Descartar
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy || !trimmed || parcelCount === 0}
          startIcon={creating ? <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} /> : null}
          sx={primaryButtonSx}
        >
          Crear lista
        </Button>
      </Box>
    </Box>
  );
}
